import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSavedProperties } from '../hooks/useSavedProperties';

const HousingCard = ({ housing, collage = true }) => {
  const { t, i18n } = useTranslation();
  const isVietnamese = i18n.language === 'vi';
  const { toggleSaved, isSaved } = useSavedProperties();
  const saved = isSaved(housing.id);

  const handleToggleSaved = (e) => {
    e.preventDefault();
    e.stopPropagation();
    toggleSaved(housing.id);
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('vi-VN', {
      style: 'currency',
      currency: 'VND',
      minimumFractionDigits: 0,
    }).format(Math.round(price));
  };

  const title = isVietnamese && housing.titleVi ? housing.titleVi : housing.title;
  const districtName = isVietnamese && housing.districtVi ? housing.districtVi : housing.district;
  const cityName = isVietnamese && housing.cityVi ? housing.cityVi : housing.city;

  // Clean description: convert literal \n to spaces, strip artifacts
  const cleanDescription = (desc) => (desc || '').replace(/\\n/g, ' ').replace(/\s*'\s*\+\s*'\s*/g, '').replace(/^'\s*|\s*'$/g, '');

  const images = (housing.images || []).filter(Boolean);
  const imgCount = Math.min(images.length, collage ? 3 : 1);

  return (
    <Link to={`/housing/${housing.id}`} className="card group">
      {/* Image collage: 1 img = full, 2 imgs = split in half, 3+ = 50% left + 2 stacked on right (collage only) */}
      <div className={`relative overflow-hidden ${collage && images.length >= 2 ? 'h-64' : 'h-52'}`}>
        {imgCount === 1 && (
          <img
            src={images[0]}
            alt={title}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
            loading="lazy"
          />
        )}
        {imgCount === 2 && (
          <div className="flex h-full gap-0.5 bg-gray-100">
            <img src={images[0]} alt={title} className="w-1/2 h-full object-cover" loading="lazy" />
            <img src={images[1]} alt={title} className="w-1/2 h-full object-cover" loading="lazy" />
          </div>
        )}
        {imgCount >= 3 && (
          <div className="flex h-full gap-0.5 bg-gray-100">
            <img
              src={images[0]}
              alt={title}
              className="w-1/2 h-full object-cover"
              loading="lazy"
            />
            <div className="flex flex-col w-1/2 h-full gap-0.5">
              <img src={images[1]} alt="" className="flex-1 min-h-0 w-full object-cover" loading="lazy" />
              <img src={images[2]} alt="" className="flex-1 min-h-0 w-full object-cover" loading="lazy" />
            </div>
          </div>
        )}
        {images.length === 0 && <div className="w-full h-full bg-gray-200" />}
        {/* Price Badge */}
        <div className="absolute top-3 left-3 bg-primary-500 text-white px-3 py-1.5 rounded-lg text-sm font-semibold shadow-lg">
          {formatPrice(housing.price)}
          <span className="text-xs opacity-80"> {t('listing.pricePerMonth')}</span>
        </div>
        {/* Save (bookmark) button */}
        <button
          type="button"
          onClick={handleToggleSaved}
          aria-label={saved ? t('saved.remove') : t('saved.save')}
          title={saved ? t('saved.remove') : t('saved.save')}
          className={`absolute top-12 right-3 w-9 h-9 rounded-full flex items-center justify-center shadow-lg transition-all duration-200 ${
            saved
              ? 'bg-primary-500 text-white'
              : 'bg-white/90 text-gray-600 hover:bg-white hover:text-primary-500'
          }`}
        >
          <svg className="w-5 h-5" fill={saved ? 'currentColor' : 'none'} viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z" />
          </svg>
        </button>
        {/* Available/Unavailable Badge */}
        <div className={`absolute top-3 right-3 px-2.5 py-1 rounded-lg text-xs font-bold shadow-lg ${
          housing.available
            ? 'bg-green-500 text-white'
            : 'bg-red-500 text-white'
        }`}>
          {housing.available ? t('detail.available') : t('detail.unavailable')}
        </div>
        {/* Featured Badge */}
        {housing.featured && (
          <div className="absolute top-3 left-3 mt-10 bg-yellow-400 text-yellow-900 px-2.5 py-1 rounded-lg text-xs font-bold shadow-lg">
            Featured
          </div>
        )}
        {/* Media count (images + videos) */}
        <div className="absolute bottom-3 right-3 bg-black/50 text-white px-2 py-0.5 rounded-md text-xs flex items-center space-x-1">
          <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
          <span>{housing.images.length}</span>
          {Array.isArray(housing.videos) && housing.videos.length > 0 && (
            <span className="flex items-center ml-1">
              <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 24 24">
                <path d="M8 5v14l11-7z" />
              </svg>
              <span>{housing.videos.length}</span>
            </span>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="p-5">
        <h3 className="font-semibold text-gray-900 text-lg mb-2 line-clamp-1 group-hover:text-primary-600 transition-colors">
          {title}
        </h3>

        {/* Location */}
        <div className="flex items-center text-gray-500 text-sm mb-3">
          <svg className="w-4 h-4 mr-1.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
          </svg>
          <span className="truncate">{districtName}, {cityName}</span>
        </div>

        {/* Brief Description */}
        <p className="text-gray-500 text-sm mb-4 line-clamp-2">
          {cleanDescription(isVietnamese && housing.descriptionVi ? housing.descriptionVi : housing.description)}
        </p>

        {/* Divider */}
        <div className="border-t border-gray-100 pt-4">
          <div className="flex items-center justify-between">
            {/* Amenities Count */}
            <div className="flex items-center space-x-1.5 text-gray-500">
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              <span className="text-sm">{housing.amenities.length} {t('detail.amenities')}</span>
            </div>
            {/* Arrow */}
            <div className="text-primary-500 group-hover:translate-x-1 transition-transform">
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </div>
          </div>
        </div>
      </div>
    </Link>
  );
};

export default HousingCard;